import { Controller } from "@hotwired/stimulus"
import {
  createHereMap,
  disposeHereMap,
  circleIcon,
  singleBubble,
} from "../helpers/here_map_helpers"
import consumer from "../channels/consumer"

// Live fleet map: renders one marker per mappable robot (initial markers from the
// server-rendered `markers` value) and keeps them current via Beam::FleetChannel.
// Each broadcast carries one robot's location + status; we move/recolor its marker,
// add one if a robot just became mappable, and drop it if it lost its location.
// Coordinates are site-based today and become real GPS once a dongle reports them —
// no change needed here, the payload shape is identical.
const STATUS_COLORS = {
  online: "#22c55e",
  offline: "#9ca3af",
  error: "#ef4444",
}

const GERMANY_CENTER = { lat: 51.1657, lng: 10.4515 }

export default class extends Controller {
  static targets = ["map"]
  static values = { markers: Array }

  connect() {
    this.markers = new Map()
    createHereMap(this.mapTarget, { center: this.computeCenter(), zoom: 6 })
      .then(({ H, map, ui }) => this.initMap(H, map, ui))
      .catch(() => {})
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    disposeHereMap(this.map)
    this.map = null
  }

  initMap(H, map, ui) {
    this.H = H
    this.map = map
    this.showBubble = singleBubble(H, ui)

    this.markersValue.forEach((data) => this.upsertMarker(data))
    this.subscribe()
  }

  subscribe() {
    this.subscription = consumer.subscriptions.create(
      { channel: "Beam::FleetChannel" },
      { received: (data) => this.upsertMarker(data) },
    )
  }

  // Create, update, or remove a marker for one robot from a {robot_id, name,
  // status, battery_percent, latitude, longitude, mappable} payload.
  upsertMarker(data) {
    if (!this.map) return

    const id = data.robot_id
    const existing = this.markers.get(id)

    if (!data.mappable || data.latitude == null || data.longitude == null) {
      if (existing) {
        this.map.removeObject(existing)
        this.markers.delete(id)
      }
      return
    }

    const position = { lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) }
    const color = STATUS_COLORS[data.status] || STATUS_COLORS.offline

    if (existing) {
      existing.setGeometry(position)
      existing.setIcon(circleIcon(color))
      existing.bauData = data
      return
    }

    const marker = new this.H.map.Marker(position, { icon: circleIcon(color) })
    marker.bauData = data
    marker.addEventListener("tap", () =>
      this.showBubble(position, this.infoContent(marker.bauData)),
    )
    this.map.addObject(marker)
    this.markers.set(id, marker)
  }

  infoContent(data) {
    const battery = data.battery_percent == null ? "—" : `${data.battery_percent} %`
    return `
      <div class="w-48 flex flex-col text-sm">
        <h3 class="font-semibold">${data.name}</h3>
        <p><strong>Status:</strong> ${data.status}</p>
        <p><strong>Batterie:</strong> ${battery}</p>
        <a href="/beam/robots/${data.robot_id}" data-turbo-frame="_top" class="text-bautask-green underline mt-2 self-end">
          Roboter anzeigen
        </a>
      </div>
    `
  }

  computeCenter() {
    const located = this.markersValue.filter((m) => m.latitude != null && m.longitude != null)
    if (!located.length) return GERMANY_CENTER

    const lats = located.map((m) => parseFloat(m.latitude))
    const lngs = located.map((m) => parseFloat(m.longitude))
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    }
  }
}
